import { useState, useEffect, useCallback, useMemo } from 'react';
import { useHistory } from './useHistory';
import { METRIC_SCALES, IMPERIAL_SCALES, DEFAULT_METRIC_SCALE_KEY, DEFAULT_IMPERIAL_SCALE_KEY } from '../constants/scales';
import { TOOLS } from '../constants/drawingTools';

export const useDrawingState = (projectName, versionName, currentImageIndex, customScales = []) => {
    // --- Core State ---
    const [selectedTool, setSelectedTool] = useState(TOOLS.SELECT);
    const [lastActiveTool, setLastActiveTool] = useState(TOOLS.POLYLINE); // Default to POLYLINE
    const [isDrawing, setIsDrawing] = useState(false);
    const [currentShape, setCurrentShape] = useState(null); // Preview shape
    const [selectionRect, setSelectionRect] = useState(null); // For area selection

    // Track the last used active drawing/measuring tool
    useEffect(() => {
        if (selectedTool && selectedTool !== TOOLS.SELECT) {
            setLastActiveTool(selectedTool);
        }
    }, [selectedTool]);

    // --- History Management ---
    const { state: allPagesData, setState: setAllPagesDataWithHistory, undo, redo, canUndo, canRedo } = useHistory({}, { limit: 100, debounceMs: 1000 });

    // --- Page-specific Shape Management ---
    const currentPageKey = useMemo(() => {
        if (!projectName || !versionName || typeof currentImageIndex !== 'number') return null;
        return `${projectName}/${versionName}/${currentImageIndex}`;
    }, [projectName, versionName, currentImageIndex]);

    const normalizeShapes = useCallback((arr) => Array.isArray(arr)
        ? arr.map(s => (s && typeof s === 'object' ? ('visible' in s ? s : { ...s, visible: true }) : s))
        : [], []);

    // Extract shapes for current page
    const shapes = useMemo(() => {
        if (!currentPageKey) return [];
        const pageData = allPagesData[currentPageKey];
        if (!pageData) return [];
        const shapesArray = Array.isArray(pageData) ? pageData : (pageData.shapes || []);
        return normalizeShapes(shapesArray);
    }, [allPagesData, currentPageKey, normalizeShapes]);

    const setShapes = useCallback((updater) => {
        if (!currentPageKey) return;
        setAllPagesDataWithHistory(prevAllPages => {
            const currentPageData = prevAllPages[currentPageKey] || { shapes: [], measurementSystem: 'metric', selectedScaleKey: DEFAULT_METRIC_SCALE_KEY };
            const currentShapesForPage = Array.isArray(currentPageData) ? currentPageData : (currentPageData.shapes || []);
            const newShapesForPage = typeof updater === 'function' ? updater(currentShapesForPage) : updater;
            const normalized = normalizeShapes(newShapesForPage);
            
            const preservedPageData = Array.isArray(currentPageData) 
                ? { shapes: normalized, measurementSystem: 'metric', selectedScaleKey: DEFAULT_METRIC_SCALE_KEY }
                : { ...currentPageData, shapes: normalized };
            
            return { ...prevAllPages, [currentPageKey]: preservedPageData };
        });
    }, [currentPageKey, setAllPagesDataWithHistory, normalizeShapes]);

    // --- Selection and Styling State ---
    const [selectedShapeIds, setSelectedShapeIds] = useState([]);
    const [drawingStrokeColor, setDrawingStrokeColor] = useState('#dc2626');
    const [displayStrokeColor, setDisplayStrokeColor] = useState('#dc2626');
    const [strokeWidth, setStrokeWidth] = useState(2);

    // --- Mode and Interaction State ---
    const [isOrthogonalModeActive, setIsOrthogonalModeActive] = useState(() => {
        const stored = localStorage.getItem('replan_isOrthogonalModeActive');
        return stored ? JSON.parse(stored) : false;
    });
    const [isRectangleMode, setIsRectangleMode] = useState(() => {
        const stored = localStorage.getItem('replan_isRectangleMode');
        return stored ? JSON.parse(stored) : false;
    });
    const [isDraggingMode, setIsDraggingMode] = useState(() => {
        const stored = localStorage.getItem('replan_isDraggingMode');
        return stored ? JSON.parse(stored) : false;
    });
    const [isSnappingEnabled, setIsSnappingEnabled] = useState(() => {
        const stored = localStorage.getItem('replan_isSnappingEnabled');
        return stored ? JSON.parse(stored) : false;
    });
    const [isGridVisible, setIsGridVisible] = useState(() => {
        const stored = localStorage.getItem('replan_isGridVisible');
        return stored ? JSON.parse(stored) : false;
    });
    // Point marker type — persisted so it survives page reloads
    const [selectedPointMarker, setSelectedPointMarker] = useState(() => {
        return localStorage.getItem('replan_selectedPointMarker') || 'disk';
    });
    const [snapIndicator, setSnapIndicator] = useState(null);
    const [visibleSnapPoints, setVisibleSnapPoints] = useState([]);
    const [vertexEditInfo, setVertexEditInfo] = useState(null);
    const [originalVertexPosition, setOriginalVertexPosition] = useState(null);
    const [insertVertexMode, setInsertVertexMode] = useState(false);
    const [hoveredSegmentInfo, setHoveredSegmentInfo] = useState(null);

    // --- Measurement and Scale State (Per-Page) ---
    const currentPageData = useMemo(() => {
        if (!currentPageKey) return null;
        const pageData = allPagesData[currentPageKey];
        if (!pageData) return null;
        return Array.isArray(pageData) ? null : pageData;
    }, [allPagesData, currentPageKey]);

    const measurementSystem = currentPageData?.measurementSystem || 'metric';
    const selectedScaleKey = currentPageData?.selectedScaleKey || 
        (measurementSystem === 'metric' ? DEFAULT_METRIC_SCALE_KEY : DEFAULT_IMPERIAL_SCALE_KEY);

    const setMeasurementSystem = useCallback((newSystem) => {
        if (!currentPageKey) return;
        setAllPagesDataWithHistory(prevAllPages => {
            const pageData = prevAllPages[currentPageKey] || { shapes: [], measurementSystem: 'metric', selectedScaleKey: DEFAULT_METRIC_SCALE_KEY };
            const updatedPageData = Array.isArray(pageData)
                ? { shapes: pageData, measurementSystem: newSystem, selectedScaleKey: pageData.selectedScaleKey || DEFAULT_METRIC_SCALE_KEY }
                : { ...pageData, measurementSystem: newSystem };
            return { ...prevAllPages, [currentPageKey]: updatedPageData };
        });
    }, [currentPageKey, setAllPagesDataWithHistory]);

    const setSelectedScaleKey = useCallback((newKey) => {
        if (!currentPageKey) return;
        setAllPagesDataWithHistory(prevAllPages => {
            const pageData = prevAllPages[currentPageKey] || { shapes: [], measurementSystem: 'metric', selectedScaleKey: DEFAULT_METRIC_SCALE_KEY };
            const updatedPageData = Array.isArray(pageData)
                ? { shapes: pageData, measurementSystem: pageData.measurementSystem || 'metric', selectedScaleKey: newKey }
                : { ...pageData, selectedScaleKey: newKey };
            return { ...prevAllPages, [currentPageKey]: updatedPageData };
        });
    }, [currentPageKey, setAllPagesDataWithHistory]);

    // --- Per-Shape Scale Locking ---
    const blockScaleForShapes = useCallback((shapeIds) => {
        setShapes(prev => prev.map(shape =>
            shapeIds.includes(shape.id)
                ? { ...shape, scaleKey: selectedScaleKey }
                : shape
        ));
    }, [setShapes, selectedScaleKey]);

    const unblockScaleForShapes = useCallback((shapeIds) => {
        setShapes(prev => prev.map(shape => {
            if (shapeIds.includes(shape.id)) {
                const { scaleKey, ...rest } = shape;
                return rest;
            }
            return shape;
        }));
    }, [setShapes]);

    const [currentScaleProps, setCurrentScaleProps] = useState(() => {
        const baseScaleList = measurementSystem === 'metric' ? METRIC_SCALES : IMPERIAL_SCALES;
        const filteredCustomScales = customScales.filter(cs => cs.documentUnit === (measurementSystem === 'metric' ? 'mm' : 'in'));
        const allScales = [...baseScaleList, ...filteredCustomScales];
        return allScales.find(s => s.key === selectedScaleKey) || baseScaleList[0];
    });

    // --- Effects for State Synchronization ---

    // Sync UI color picker with selection
    useEffect(() => {
        if (selectedShapeIds.length > 0) {
            const firstSelectedShape = shapes.find(s => s.id === selectedShapeIds[0]);
            if (firstSelectedShape && firstSelectedShape.stroke) {
                setDisplayStrokeColor(firstSelectedShape.stroke);
            }
        } else {
            setDisplayStrokeColor(drawingStrokeColor);
        }
    }, [selectedShapeIds, shapes, drawingStrokeColor]);

    // Update scale properties when system or key changes
    useEffect(() => {
        const baseScaleList = measurementSystem === 'metric' ? METRIC_SCALES : IMPERIAL_SCALES;
        const filteredCustomScales = customScales.filter(cs => cs.documentUnit === (measurementSystem === 'metric' ? 'mm' : 'in'));
        const scaleList = [...baseScaleList, ...filteredCustomScales];
        let newSelectedKey = selectedScaleKey;

        const allScales = [...METRIC_SCALES, ...IMPERIAL_SCALES, ...customScales];
        const isValidKey = allScales.some(s => s.key === newSelectedKey);
        
        if (!isValidKey) {
            newSelectedKey = measurementSystem === 'metric' ? DEFAULT_METRIC_SCALE_KEY : DEFAULT_IMPERIAL_SCALE_KEY;
            setSelectedScaleKey(newSelectedKey);
        }
        
        const newProps = scaleList.find(s => s.key === newSelectedKey) || baseScaleList[0];
        setCurrentScaleProps(newProps);
    }, [measurementSystem, selectedScaleKey, customScales]);

    // --- Persistence Effects for Drawing Modes ---
    useEffect(() => {
        localStorage.setItem('replan_isOrthogonalModeActive', JSON.stringify(isOrthogonalModeActive));
    }, [isOrthogonalModeActive]);

    useEffect(() => {
        localStorage.setItem('replan_isRectangleMode', JSON.stringify(isRectangleMode));
    }, [isRectangleMode]);

    useEffect(() => {
        localStorage.setItem('replan_isDraggingMode', JSON.stringify(isDraggingMode));
    }, [isDraggingMode]);

    useEffect(() => {
        localStorage.setItem('replan_isSnappingEnabled', JSON.stringify(isSnappingEnabled));
    }, [isSnappingEnabled]);

    useEffect(() => {
        localStorage.setItem('replan_isGridVisible', JSON.stringify(isGridVisible));
    }, [isGridVisible]);

    useEffect(() => {
        localStorage.setItem('replan_selectedPointMarker', selectedPointMarker);
    }, [selectedPointMarker]);


    return {
        // Core State
        selectedTool, setSelectedTool,
        lastActiveTool,
        isDrawing, setIsDrawing,
        currentShape, setCurrentShape,
        selectionRect, setSelectionRect,
        // History
        undo, redo, canUndo, canRedo,
        // Shapes
        shapes, setShapes,
        // Selection & Style
        selectedShapeIds, setSelectedShapeIds,
        drawingStrokeColor, setDrawingStrokeColor,
        displayStrokeColor,
        strokeWidth, setStrokeWidth,
        // Modes & Interactions
        isOrthogonalModeActive, setIsOrthogonalModeActive,
        isRectangleMode, setIsRectangleMode,
        isDraggingMode, setIsDraggingMode,
        isSnappingEnabled, setIsSnappingEnabled,
        isGridVisible, setIsGridVisible,
        snapIndicator, setSnapIndicator,
        visibleSnapPoints, setVisibleSnapPoints,
        vertexEditInfo, setVertexEditInfo,
        originalVertexPosition, setOriginalVertexPosition,
        insertVertexMode, setInsertVertexMode,
        hoveredSegmentInfo, setHoveredSegmentInfo,
        // Point marker
        selectedPointMarker, setSelectedPointMarker,
        // Measurement & Scale
        measurementSystem, setMeasurementSystem,
        selectedScaleKey, setSelectedScaleKey,
        currentScaleProps,
        blockScaleForShapes, unblockScaleForShapes,
    };
};